import '@ui5/webcomponents-react';
import { IssueType, TranslationType, Worklist } from 'generated/graphql';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useABAPQueryStore } from 'stores/CreateABAPQueryStore';
import { useBaseQueryStore } from 'stores/CreateBaseQueryStore';
import { useCorporateContentQueryStore } from 'stores/CreateCorporateContentQueryStore';
import { useDitaQueryStore } from 'stores/CreateDitaQueryStore';
import { useMarketingQueryStore } from 'stores/CreateMarketingQueryStore';
import { useNonAbapQueryStore } from 'stores/CreateNonAbapQueryStore';
import { useNotesQueryStore } from 'stores/CreateNotesQueryStore';
import { useCreateQueryFreezeStore } from 'stores/CreateQueryFreezeStore';
import { useTrainingQueryStore } from 'stores/CreateTrainingQueryStore';
import { NotSelectedTranslationType } from 'stores/interfaces/CommonQueryState';
import { filterAllowedTranslationTypes } from 'utils/filterAllowedTranslationTypes';
import { useGetDate } from './useGetDate';

import ValueState from '@ui5/webcomponents-base/dist/types/ValueState.js';

type SetParam<T> = (value: T) => void;

export const useParametrizeQueryCreation = () => {
  const store = useBaseQueryStore();
  const freezeStore = useCreateQueryFreezeStore();
  const ditaStore = useDitaQueryStore();
  const abapStore = useABAPQueryStore();
  const nonAbapStore = useNonAbapQueryStore();
  const corporateContentStore = useCorporateContentQueryStore();
  const trainingStore = useTrainingQueryStore();
  const notesStore = useNotesQueryStore();
  const marketingStore = useMarketingQueryStore();
  const location = useLocation().search;
  const { isDeadlineValid } = useGetDate();
  const query = new URLSearchParams(location);

  const [isTranslationTypeValid, setIsTranslationTypeValid] = useState<boolean>(false);

  useEffect(() => {
    if (store.projectNames && isTranslationTypeValid && store.projectNames.fetching === false) {
      const project = query.get('project');

      if (project === null) {
        return;
      }

      const validProject = store.projectNames.data?.projects.find((p) => p.projectId === project);
      if (validProject !== undefined) {
        store.setLanguages(
          validProject?.languages.map((projectLanguage) => {
            return projectLanguage?.language;
          }) || []
        );

        store.setSelectedProject(Number(validProject.id));
        handleParam('targetLanguage', store.setTargetLanguage);
      }
    }
  }, [store.projectNames?.data?.projects, isTranslationTypeValid]);

  useEffect(() => {
    const edit = query.get('edit');
    const isEdit = edit?.toLowerCase() === 'true';
    if (freezeStore.setEditParam) {
      freezeStore.setEditParam(isEdit);
    }
  }, []);

  type Validator = (value: string) => boolean;

  const createEnumValidator = (enumValues: string[]): Validator => {
    return (value: string): boolean => {
      return enumValues.includes(value);
    };
  };

  const isNumericalString = (value: string) => /^\d+$/.test(value);

  const handleParam = <T,>(
    param: string,
    setParam: SetParam<T>,
    setFreeze?: SetParam<boolean>,
    validate?: Validator
  ): void => {
    const value = query.get(param);
    const isValueEmpty = value === null || value.trim().length === 0;
    if (isValueEmpty || (validate && !validate(value))) {
      return;
    }

    setParam(value as T);
    if (setFreeze) {
      setFreeze(!freezeStore.editParam);
    }
  };

  const getCommonParams = (): void => {
    handleParam('sourceText', store.setSourceText, freezeStore.setSourceText);
    handleParam('targetText', store.setTargetText);
    handleParam('details', store.setDetails);

    const deadline = query.get('deadline') ?? '';
    if (isDeadlineValid(deadline)) {
      store.setDeadline(deadline);
      store.setAnswerDeadline(deadline);
    }

    const issueType = query.get('issueType') ?? '';
    if (Object.values(IssueType).includes(issueType as IssueType)) {
      store.setSelectedIssueType(issueType as IssueType);
    }
  };

  const getDitaQueryParams = (): void => {
    handleParam('xtmProjectName', ditaStore.setXtmProjectName, freezeStore.setXtmProjectName);
    handleParam('fileTitle', ditaStore.setFileTitle, freezeStore.setFileTitle);
    handleParam('fileName', ditaStore.setFileName, freezeStore.setFileName);
    handleParam('submap', ditaStore.setSubmap, freezeStore.setSubmap);
    handleParam('deliverable', ditaStore.setDeliverable, freezeStore.setDeliverable);
    handleParam('cms', ditaStore.setCmsProject, freezeStore.setCmsProject);
    handleParam('loio', ditaStore.setLoio, freezeStore.setLoio);
    handleParam('sourceContact', ditaStore.setSourceContact, freezeStore.setSourceContact);
  };

  const getABAPQueryParams = (): void => {
    handleParam('system', abapStore.setSystem, freezeStore.setSystem);
    handleParam('translationArea', abapStore.setTranslationArea, freezeStore.setTranslationArea);
    handleParam('objectType', abapStore.setObjectType, freezeStore.setObjectType);
    handleParam('objectName', abapStore.setObjectName, freezeStore.setObjectName);
    handleParam(
      'collectionPackage',
      abapStore.setCollectionPackage,
      freezeStore.setCollectionPackage
    );
    handleParam(
      'applicationComponent',
      abapStore.setApplicationComponent,
      freezeStore.setApplicationComponent
    );
    handleParam(
      'repositoryProgramId',
      abapStore.setRepositoryProgramId,
      freezeStore.setRepositoryProgramId
    );
    handleParam(
      'repositoryProgramType',
      abapStore.setRepositoryProgramType,
      freezeStore.setRepositoryProgramType
    );
    handleParam(
      'repositoryProgramName',
      abapStore.setRepositoryProgramName,
      freezeStore.setRepositoryProgramName
    );
    handleParam('objectOwner', abapStore.setObjectOwner, freezeStore.setObjectOwner);
    handleParam('collectionOwner', abapStore.setCollectionOwner, freezeStore.setCollectionOwner);
    handleParam('languagesTranslatorTeams', abapStore.setLanguagesTranslatorTeams);
    handleParam('maxLength', abapStore.setMaxLength, freezeStore.setMaxLength, isNumericalString);
  };

  const getNonABAPQueryParams = (): void => {
    handleParam('xtmProjectName', nonAbapStore.setXtmProjectName, freezeStore.setXtmProjectName);
    handleParam('repository', nonAbapStore.setRepository, freezeStore.setRepository);
    handleParam('collection', nonAbapStore.setCollection, freezeStore.setCollection);
    handleParam('fileName', nonAbapStore.setFileName, freezeStore.setFileName);
    handleParam('stringId', nonAbapStore.setStringId, freezeStore.setStringId);
    handleParam('sourceContact', nonAbapStore.setSourceContact, freezeStore.setSourceContact);
  };

  const getCorporateContentQueryParams = (): void => {
    handleParam(
      'xtmProjectName',
      corporateContentStore.setXtmProjectName,
      freezeStore.setXtmProjectName
    );
    handleParam('fileName', corporateContentStore.setFileName, freezeStore.setFileName);
  };

  const getTrainingQueryParams = (): void => {
    handleParam('xtmProjectName', trainingStore.setXtmProjectName, freezeStore.setXtmProjectName);
    handleParam('fileName', trainingStore.setFileName, freezeStore.setFileName);
  };

  const getNotesQueryParams = (): void => {
    handleParam('documentNumber', notesStore.setDocumentNumber, freezeStore.setDocumentNumber);
    handleParam('component', notesStore.setComponent, freezeStore.setComponent);
    handleParam(
      'worklist',
      notesStore.setWorklist,
      freezeStore.setWorklist,
      createEnumValidator(Object.values(Worklist))
    );
    handleParam(
      'documentResponsible',
      notesStore.setDocumentResponsible,
      freezeStore.setDocumentResponsible
    );
  };

  const getMarketingQueryParams = (): void => {
    handleParam('account', marketingStore.setAccount, freezeStore.setAccount);
    handleParam('fileName', marketingStore.setFileName, freezeStore.setFileName);
  };

  const fillCreateQueryFormWithParams = (): void => {
    getCommonParams();
    const translationType = query.get('translationType');
    if (translationType === null) {
      return;
    }

    if (
      !Object.values(TranslationType).includes(translationType as TranslationType) ||
      !filterAllowedTranslationTypes(translationType as TranslationType)
    ) {
      store.setSelectedTranslationType(NotSelectedTranslationType.NOT_SELECTED_TRANSLATION_TYPE);
      store.setTranslationTypeValueState(ValueState.Negative);
      return;
    }
    setIsTranslationTypeValid(true);
    store.setSelectedTranslationType(translationType as TranslationType);

    switch (translationType as TranslationType) {
      case TranslationType.DITA:
        getDitaQueryParams();
        break;
      case TranslationType.ABAP:
        getABAPQueryParams();
        break;
      case TranslationType.NON_ABAP:
        getNonABAPQueryParams();
        break;
      case TranslationType.CORPORATE_CONTENT:
        getCorporateContentQueryParams();
        break;
      case TranslationType.TRAINING:
        getTrainingQueryParams();
        break;
      case TranslationType.NOTES:
        getNotesQueryParams();
        break;
      case TranslationType.MARKETING:
        getMarketingQueryParams();
        break;
    }
  };

  return {
    fillCreateQueryFormWithParams
  };
};
